// v2/pages/admin/AdminAnnouncementsV2.js — gorsel duyuru (banner) yonetimi
// Liste sirasi = ana sayfadaki donus sirasi (sira, sonra en yeni).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, AlertTriangle, RotateCw,
  Megaphone, Link2, CalendarDays, Info, ImageOff,
} from 'lucide-react';
import useAdminAnnouncements, {
  adminAnnouncementApi, itemdenAlanlar,
} from '../../hooks/useAdminAnnouncements';
import AnnouncementModal from '../../components/admin/AnnouncementModal';
import ToastStack from '../../components/Toast';
import useToast from '../../hooks/useToast';
import p from '../../styles/AdminAnnouncementsV2.module.css';
import '../../styles/tokens.css';

const DURUM = {
  yayinda: { etiket: 'Yayında', cls: 'stOn' },
  planli: { etiket: 'Planlı', cls: 'stPlan' },
  bitti: { etiket: 'Süresi doldu', cls: 'stOff' },
  pasif: { etiket: 'Pasif', cls: 'stOff' },
};
const SAYAC_SIRASI = ['yayinda', 'planli', 'pasif', 'bitti'];

function trTarih(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function aralik(it) {
  if (!it.start && !it.end) return 'Süresiz';
  if (it.start && it.end) return `${trTarih(it.start)} – ${trTarih(it.end)}`;
  if (it.start) return `${trTarih(it.start)} itibarıyla`;
  return `${trTarih(it.end)} tarihine kadar`;
}

function Kart({ it, ilk, son, mesgul, onYukari, onAsagi, onAktif, onDuzenle, onSil }) {
  const [onay, setOnay] = useState(false);
  const [bozuk, setBozuk] = useState(false);
  const d = DURUM[it.status] || DURUM.pasif;

  useEffect(() => { setBozuk(false); }, [it.image]);
  useEffect(() => {
    if (!onay) return undefined;
    const t = setTimeout(() => setOnay(false), 4000);
    return () => clearTimeout(t);
  }, [onay]);

  return (
    <article className={`${p.card} ${!it.active ? p.cardOff : ''}`}>
      <div className={p.thumb}>
        {bozuk ? (
          <span className={p.thumbErr}><ImageOff size={26} /> Görsel yüklenemedi</span>
        ) : (
          <img src={it.image} alt={it.title || 'Duyuru'} loading="lazy"
               onError={() => setBozuk(true)} />
        )}
        <span className={`${p.status} ${p[d.cls]}`}>{d.etiket}</span>
      </div>

      <div className={p.cardBody}>
        <h3 className={p.cardTitle} title={it.title}>
          {it.title || <i className={p.muted}>Başlıksız</i>}
        </h3>
        <div className={p.meta}><CalendarDays size={13} /> <span>{aralik(it)}</span></div>
        <div className={p.meta}>
          <Link2 size={13} />
          {it.href ? (
            <a className={p.link} href={it.href} target="_blank"
               rel="noopener noreferrer" title={it.href}>{it.href}</a>
          ) : <span className={p.muted}>Link yok</span>}
        </div>
        {it.createdBy && <div className={p.by}>Ekleyen: {it.createdBy}</div>}
      </div>

      <div className={p.actions}>
        <span className={p.order}>
          <button type="button" onClick={onYukari} disabled={ilk || mesgul} aria-label="Yukarı taşı">
            <ArrowUp size={15} />
          </button>
          <button type="button" onClick={onAsagi} disabled={son || mesgul} aria-label="Aşağı taşı">
            <ArrowDown size={15} />
          </button>
        </span>
        <label className={p.switchWrap}>
          <input type="checkbox" className={p.switch} checked={it.active}
                 disabled={mesgul} onChange={onAktif} aria-label="Aktif / pasif" />
          <span>{it.active ? 'Aktif' : 'Pasif'}</span>
        </label>
        <span className={p.spacer} />
        {mesgul && <Loader2 size={15} className={p.spin} />}
        <button type="button" className={p.iconBtn} onClick={onDuzenle}
                disabled={mesgul} aria-label="Düzenle">
          <Pencil size={15} />
        </button>
        {onay ? (
          <button type="button" className={p.delConfirm} disabled={mesgul}
                  onClick={() => { setOnay(false); onSil(); }}>
            Silinsin mi?
          </button>
        ) : (
          <button type="button" className={`${p.iconBtn} ${p.delBtn}`}
                  onClick={() => setOnay(true)} disabled={mesgul} aria-label="Sil">
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </article>
  );
}

export default function AdminAnnouncementsV2() {
  const { data, loading, error, yenile } = useAdminAnnouncements();
  const toast = useToast();
  const [liste, setListe] = useState([]);
  const [mesgul, setMesgul] = useState(null);   // islemdeki duyuru id'si ya da 'sira'
  const [modal, setModal] = useState({ acik: false, duyuru: null });

  useEffect(() => { if (data) setListe(data.items); }, [data]);

  const sayac = useMemo(() => liste.reduce((acc, it) => {
    acc[it.status] = (acc[it.status] || 0) + 1;
    return acc;
  }, {}), [liste]);

  const sinir = (data && data.limits) || { max: 10, maxActive: 5 };
  const bugun = (data && data.today) || '';
  const yayinSayisi = (sayac.yayinda || 0) + (sayac.planli || 0);
  const kotaDolu = liste.length >= sinir.max;
  const yayinBos = sinir.maxActive - yayinSayisi;

  const yeniAc = useCallback(() => setModal({ acik: true, duyuru: null }), []);
  const kapat = useCallback(() => setModal({ acik: false, duyuru: null }), []);
  const kaydedildi = useCallback((mesaj) => {
    setModal({ acik: false, duyuru: null });
    toast.push(mesaj);
    yenile();
  }, [toast, yenile]);

  const tasi = useCallback(async (i, yon) => {
    const j = i + yon;
    if (mesgul || j < 0 || j >= liste.length) return;
    const onceki = liste;
    const yeni = [...liste];
    [yeni[i], yeni[j]] = [yeni[j], yeni[i]];
    setListe(yeni);
    setMesgul('sira');
    try {
      const v = await adminAnnouncementApi.sirala(yeni.map((x) => x.id));
      if (!v || !v.success) throw new Error((v && v.message) || 'Sıra kaydedilemedi');
      yenile();
    } catch (e) {
      setListe(onceki);
      toast.push((e && e.message) || 'Sıra kaydedilemedi');
    } finally {
      setMesgul(null);
    }
  }, [liste, mesgul, yenile, toast]);

  const aktifDegis = useCallback(async (it) => {
    if (mesgul) return;
    const acilacak = !it.active && (!it.end || !bugun || it.end >= bugun);
    if (acilacak && yayinBos <= 0) {
      toast.push(`En fazla ${sinir.maxActive} duyuru yayında olabilir; önce birini pasife alın`);
      return;
    }
    setMesgul(it.id);
    try {
      const v = await adminAnnouncementApi.guncelle(
        it.id, { ...itemdenAlanlar(it), aktif: !it.active }, null,
      );
      if (!v || !v.success) throw new Error((v && v.message) || 'Güncellenemedi');
      setListe((l) => l.map((x) => (x.id === it.id ? v.item : x)));
      toast.push(v.item.active ? 'Duyuru aktifleştirildi' : 'Duyuru pasife alındı');
    } catch (e) {
      toast.push((e && e.message) || 'Güncellenemedi');
    } finally {
      setMesgul(null);
    }
  }, [mesgul, toast, bugun, yayinBos, sinir.maxActive]);

  const sil = useCallback(async (it) => {
    if (mesgul) return;
    setMesgul(it.id);
    try {
      const v = await adminAnnouncementApi.sil(it.id);
      if (!v || !v.success) throw new Error((v && v.message) || 'Silinemedi');
      setListe((l) => l.filter((x) => x.id !== it.id));
      toast.push('Duyuru silindi');
    } catch (e) {
      toast.push((e && e.message) || 'Silinemedi');
    } finally {
      setMesgul(null);
    }
  }, [mesgul, toast]);

  let icerik;
  if (loading && !data) {
    icerik = <div className={p.state}><Loader2 size={18} className={p.spin} /> Yükleniyor…</div>;
  } else if (error && !data) {
    icerik = (
      <div className={`${p.state} ${p.stateErr}`}>
        <AlertTriangle size={18} /> <span>{error}</span>
        <button type="button" className={p.retry} onClick={yenile}>
          <RotateCw size={14} /> Tekrar dene
        </button>
      </div>
    );
  } else if (liste.length === 0) {
    icerik = (
      <div className={p.empty}>
        <Megaphone size={34} strokeWidth={1.4} />
        <b>Henüz duyuru yok</b>
        <span>İlk banner'ı ekleyerek başlayın.</span>
        <button type="button" className={p.addBtn} onClick={yeniAc}>
          <Plus size={16} /> Yeni Duyuru
        </button>
      </div>
    );
  } else {
    icerik = (
      <div className={p.grid}>
        {liste.map((it, i) => (
          <Kart
            key={it.id} it={it}
            ilk={i === 0} son={i === liste.length - 1}
            mesgul={mesgul === it.id || mesgul === 'sira'}
            onYukari={() => tasi(i, -1)}
            onAsagi={() => tasi(i, 1)}
            onAktif={() => aktifDegis(it)}
            onDuzenle={() => setModal({ acik: true, duyuru: it })}
            onSil={() => sil(it)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={p.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />

      <header className={p.head}>
        <div>
          <h1 className={p.title}>Duyurular</h1>
          <p className={p.sub}>Ana sayfadaki duyuru kartında gösterilen görseller</p>
        </div>
        <button
          type="button" className={p.addBtn} onClick={yeniAc} disabled={kotaDolu}
          title={kotaDolu ? `En fazla ${sinir.max} duyuru eklenebilir; önce birini silin` : undefined}
        >
          <Plus size={16} /> Yeni Duyuru
        </button>
      </header>

      <div className={p.info}>
        <Info size={15} />
        <span>
          Yatay görsel kullanın: önerilen <b>1200 × 750 px</b> (8:5). JPG, PNG veya WEBP,
          en fazla <b>3 MB</b>. Kart farklı ekranlarda kenarlardan kırpılabilir; önemli
          yazıları görselin ortasına yerleştirin. Birden fazla duyuru yayındaysa ana
          sayfada sırayla döner; sıra bu listeyle aynıdır.
        </span>
      </div>

      {liste.length > 0 && (
        <div className={p.counts}>
          <span className={`${p.count} ${p.quota} ${kotaDolu ? p.quotaFull : ''}`}>
            Görsel: {liste.length} / {sinir.max}
          </span>
          <span className={`${p.count} ${p.quota} ${yayinBos <= 0 ? p.quotaFull : ''}`}>
            Yayında + planlı: {yayinSayisi} / {sinir.maxActive}
          </span>
          {SAYAC_SIRASI.filter((k) => sayac[k]).map((k) => (
            <span key={k} className={`${p.count} ${p[DURUM[k].cls]}`}>
              {DURUM[k].etiket}: {sayac[k]}
            </span>
          ))}
        </div>
      )}

      {icerik}

      <AnnouncementModal
        acik={modal.acik} duyuru={modal.duyuru}
        onKapat={kapat} onKaydedildi={kaydedildi}
        yayinBos={yayinBos}
      />
    </div>
  );
}
